'use client'
import { useState } from 'react'
import { Slider, Button } from '@achieve/ascend'
import { AchieveLink } from 'components/AchieveLink'
import { Breadcrumbs } from 'components/Breadcrumbs/Breadcrumbs'
import { Lock } from '@mui/icons-material'
import Image from 'next/image'

// Map of state slugs to their image filenames
const STATE_FLAG_MAP = {
  'new-york': 'new-york-state-flag-circle-round-26273_64.png',
  alabama: 'alabama-state-circle-round-flag-26221_64.png',
  alaska: 'alaska-state-circle-round-flag-26222_64.png',
  arizona: 'arizona-state-circle-round-flag-26223_64.png',
  arkansas: 'arkansas-state-flag-circle-round-26224_64.png',
  california: 'california-state-flag-circle-round-26225_64.png',
  colorado: 'colorado-state-flag-circle-round-26226_64.png',
  connecticut: 'connecticut-state-flag-circle-round-26227_64.png',
  delaware: 'delaware-state-flag-circle-round-26228_64.png',
  florida: 'florida-state-flag-circle-round-26229_64.png',
  georgia: 'georgia-state-flag-circle-round-26230_64.png',
  hawaii: 'hawai-state-flag-circle-round-26231_64.png',
  idaho: 'idaho-state-flag-circle-round-26232_64.png',
  illinois: 'illinois-state-flag-circle-round-26233_64.png',
  indiana: 'indiana-state-flag-circle-round-26234_64.png',
  iowa: 'iowa-state-flag-circle-round-26235_64.png',
  kansas: 'kansas-state-flag-circle-round-26236_64.png',
  kentucky: 'kentucky-state-flag-circle-round-26237_64.png',
  louisiana: 'louisiana-state-flag-circle-round-26238_64.png',
  maine: 'maine-state-flag-circle-round-26239_64.png',
  maryland: 'maryland-state-flag-circle-round-26240_64.png',
  massachusetts: 'massachusetts-state-flag-circle-round-26241_64.png',
  michigan: 'michigan-state-flag-circle-round-26242_64.png',
  minnesota: 'minnesota-state-flag-circle-round-26243_64.png',
  mississippi: 'mississippi-state-flag-circle-round-26244_64.png',
  missouri: 'missouri-state-flag-circle-round-26245_64.png',
  montana: 'montana-state-flag-circle-round-26246_64.png',
  nebraska: 'nebraska-state-flag-circle-round-26247_64.png',
  nevada: 'nevada-state-flag-circle-round-26248_64.png',
  'new-hampshire': 'new-hampshire-state-flag-circle-round-26249_64.png',
  'new-jersey': 'new-jersey-state-flag-circle-round-26250_64.png',
  'new-mexico': 'new-mexico-state-flag-circle-round-26251_64.png',
  'north-carolina': 'north-carolina-state-flag-circle-round-26254_64.png',
  'north-dakota': 'north-dakota-state-flag-circle-round-26255_64.png',
  ohio: 'ohio--state-flag-circle-round-26256_64.png',
  oklahoma: 'oklahoma-state-flag-circle-round-26257_64.png',
  oregon: 'oregon-state-flag-circle-round-26258_64.png',
  pennsylvania: 'pennsylvania-state-flag-circle-round-26260_64.png',
  'rhode-island': 'rhode-island-state-flag-circle-round-26261_64.png',
  'south-carolina': 'south-carolina-state-flag-circle-roound-26262_64.png',
  'south-dakota': 'south-dakota-state-flag-circle-round-26263_64.png',
  tennessee: 'tennessee-state-flag-circle-round-26264_64.png',
  texas: 'texas-state-flag-circle-round-26265_64.png',
  utah: 'utah-state-flag-circle-round-26266_64.png',
  vermont: 'vermont-state-flag-circle-round-26267_64.png',
  virginia: 'virginia-state-flag-circle-round-26268_64.png',
  washington: 'washington-state-flag-circle-round-26269_64.png',
  'west-virginia': 'west-virginia-state-flag-circle-round-26270_64.png',
  wisconsin: 'wisconsin-state-flag-circle-round-26271_64.png',
  wyoming: 'wyoming-state-flag-circle-round-26272_64.png',
}

export function StateHero({ stateName, stateSlug, heroImageLink }) {
  const [debtAmount, setDebtAmount] = useState(20000)
  const dollarUSLocale = Intl.NumberFormat('en-US')

  const minValue = 5000
  const maxValue = 150000

  const handleSliderChange = (e) => {
    setDebtAmount(e.target.value)
  }

  // Get the correct flag image filename
  // Normalize slug to lowercase and replace spaces with hyphens
  const normalizedSlug = stateSlug?.toLowerCase().trim().replace(/\s+/g, '-')
  const flagImage = normalizedSlug ? STATE_FLAG_MAP[normalizedSlug] : null

  // Debug: log if slug doesn't match any state
  if (process.env.NODE_ENV === 'development' && stateSlug && !flagImage) {
    console.warn(`State flag not found for slug: "${stateSlug}" (normalized: "${normalizedSlug}")`)
    console.warn('Available states:', Object.keys(STATE_FLAG_MAP).join(', '))
  }

  return (
    <div className="w-full bg-white">
      {/* Breadcrumbs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <Breadcrumbs className="text-sm">
          <AchieveLink href="/" className="text-blue-600 hover:text-blue-800">
            <span className="flex items-center gap-1">
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                />
              </svg>
            </span>
          </AchieveLink>
          <AchieveLink href="/help-near-me" className="text-blue-600 hover:text-blue-800">
            Help Near Me
          </AchieveLink>
          <span className="text-gray-600">{stateName || 'State name here'}</span>
        </Breadcrumbs>
      </div>

      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
        {/* State Info */}
        <div className="flex items-center gap-4 mb-6">
          {/* State Flag Image */}
          <div className="w-16 h-16 lg:w-20 lg:h-20 rounded-lg bg-gray-200 shrink-0 relative overflow-hidden">
            {flagImage && (
              <Image
                src={`/states/${flagImage}`}
                alt={`${stateName || stateSlug} flag`}
                fill
                className="object-cover"
                sizes="(max-width: 1024px) 64px, 80px"
              />
            )}
          </div>

          {/* State Title */}
          <div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
              {stateName || 'State name goes here'}
            </h1>
          </div>
        </div>

        {/* Content Area - Debt Calculator Card */}
        <div className="w-full flex flex-col lg:flex-row gap-4 lg:gap-[22px]">
          {/* Hero Image or Placeholder */}
          <div className="flex-1">
            {heroImageLink && heroImageLink.trim() !== '' ? (
              <div className="relative h-full min-h-[280px] rounded-md overflow-hidden">
                <Image
                  src={heroImageLink}
                  alt={`${stateName || 'State'} hero image`}
                  fill
                  className="object-cover"
                  sizes="(max-width: 1024px) 100vw, 800px"
                  priority
                />
              </div>
            ) : (
              <div className="bg-gray-100 h-full min-h-[280px] rounded-md"></div>
            )}
          </div>

          {/* Calculator Card */}
          <div
            className="rounded-[10px] p-3 bg-white w-full lg:w-auto lg:max-w-[280px] flex flex-col gap-[10px] relative"
            style={{
              border: '2px solid transparent',
              backgroundImage:
                'linear-gradient(white, white), linear-gradient(to bottom, #3300FF, #1AB1E2)',
              backgroundOrigin: 'border-box',
              backgroundClip: 'padding-box, border-box',
            }}
          >
            {/* Lock Icon */}
            <div className="w-9 h-9 rounded-md bg-blue-100 flex items-center justify-center self-center">
              <Lock sx={{ fontSize: 20, color: '#2563eb' }} />
            </div>

            {/* Title */}
            <h2 className="text-center text-gray-700 text-sm font-normal w-full">
              How much debt do you have?
            </h2>

            {/* Amount Display */}
            <div className="text-center w-full">
              <span className="text-3xl font-bold text-gray-900">
                ${dollarUSLocale.format(debtAmount)}
              </span>
            </div>

            {/* Slider */}
            <div className="w-full px-5">
              <Slider
                aria-label="debt-amount-slider"
                value={debtAmount}
                onChange={handleSliderChange}
                min={minValue}
                max={maxValue}
                step={1000}
                className="w-full"
                classes={{
                  mark: 'hidden',
                }}
                marks={[
                  {
                    value: minValue,
                    label: (
                      <span className="text-[10px] text-gray-600 font-medium">
                        ${dollarUSLocale.format(minValue)}
                      </span>
                    ),
                  },
                  {
                    value: maxValue,
                    label: (
                      <span className="text-[10px] text-gray-600 font-medium">
                        ${dollarUSLocale.format(maxValue)}
                      </span>
                    ),
                  },
                ]}
              />
            </div>

            {/* CTA Button */}
            <Button
              variant="contained"
              color="primary"
              className="w-full! bg-blue-700! hover:bg-blue-800! text-white! font-semibold! py-2.5! rounded-md! text-sm! normal-case!"
            >
              Get started
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
